import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ramadanService, RamadanExpose, Comment } from '../services/ramadanService';
import { useAuth } from '../context/AuthContext';

export const RamadanDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expose, setExpose] = useState<RamadanExpose | null>(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);
  
  // États pour le contrôle des médias
  const [mediaMode, setMediaMode] = useState<'video' | 'external'>('video');
  const [currentVideo, setCurrentVideo] = useState<string | null>(null);

  const getStatusLabel = (statusAuto?: string, status?: string) => {
    // Utiliser status_auto en priorité, sinon fallback sur status
    const currentStatus = statusAuto || status || 'a_venir';
    const labels: { [key: string]: { text: string; icon: string; color: string } } = {
      a_venir: { text: 'À venir', icon: '⏳', color: 'bg-blue-100 text-blue-800' },
      en_cours: { text: 'En cours', icon: '🟢', color: 'bg-green-100 text-green-800' },
      termine: { text: 'Terminé', icon: '✅', color: 'bg-gray-100 text-gray-800' },
      passe: { text: 'Passé', icon: '📜', color: 'bg-gray-100 text-gray-800' },
    };
    return labels[currentStatus] || labels['a_venir'];
  };

  useEffect(() => {
    if (id) {
      loadExpose();
      ramadanService.incrementView(id).catch(() => {
        // Erreur silencieuse
      });
    }
  }, [id]);

  const loadExpose = async () => {
    if (!id) return;
    try {
      const data = await ramadanService.getById(id);
      setExpose(data);
      if (data.mini_video && !currentVideo) {
        setCurrentVideo(data.mini_video);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!user || !id) return;
    try {
      await ramadanService.like(id);
      loadExpose();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !id || !comment.trim()) return;
    
    setSubmitting(true);
    try {
      await ramadanService.comment(id, comment);
      setComment('');
      setTimeout(() => {
        loadExpose();
      }, 500);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'ajout du commentaire. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.contenu);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleSaveEdit = async (commentId: number) => {
    if (!editingCommentText.trim()) return;
    
    setSubmitting(true);
    try {
      await ramadanService.updateComment(commentId, editingCommentText);
      setEditingCommentId(null);
      setEditingCommentText('');
      setTimeout(() => {
        loadExpose();
      }, 500);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la modification du commentaire. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce commentaire ?')) return;
    
    setDeletingCommentId(commentId);
    try {
      await ramadanService.deleteComment(commentId);
      setTimeout(() => {
        loadExpose();
      }, 500);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression du commentaire. Veuillez réessayer.');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleVideoChange = (url: string) => {
    if (!url) return;
    
    if (mediaMode === 'video') {
      setCurrentVideo(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="relative">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-purple-200 border-t-purple-700"></div>
        </div>
      </div>
    );
  }

  if (!expose) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-8xl mb-6">🌙</div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Exposé Ramadan non trouvé</h2>
          <Link to="/ramadan" className="text-purple-700 hover:text-purple-800 font-semibold">
            ← Retour aux exposés Ramadan
          </Link>
        </div>
      </div>
    );
  }

  const hasMedia = expose.mini_video || expose.lien_youtube || expose.lien_tiktok || expose.lien_instagram;

  return (
    <div className="min-h-screen">
      {/* Hero Section avec Mini-Vidéo ou Image */}
      <section className="relative bg-gradient-to-br from-purple-50 via-green-50/30 to-green-100/50 py-12 overflow-hidden">
        {/* Motifs décoratifs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Bouton retour */}
          <button
            onClick={() => navigate('/ramadan')}
            className="mb-6 inline-flex items-center space-x-2 text-gray-700 hover:text-purple-700 transition-colors font-medium"
          >
            <span>←</span>
            <span>Retour aux exposés Ramadan</span>
          </button>

          {/* Card principale */}
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-purple-100/50 overflow-hidden">
            {/* Header avec Mini-Vidéo ou Image */}
            <div className="relative h-96 sm:h-[500px] bg-gradient-to-br from-purple-600 via-purple-700 to-green-700 overflow-hidden">
              {(() => {
                if (mediaMode === 'video' && currentVideo) {
                  const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
                  const match = currentVideo.match(youtubeRegex);
                  
                  if (match) {
                    return (
                      <iframe
                        key={currentVideo}
                        src={`https://www.youtube.com/embed/${match[1]}`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Vidéo YouTube"
                      ></iframe>
                    );
                  }
                  
                  if (currentVideo === expose.mini_video || currentVideo.startsWith('/') || currentVideo.startsWith('http')) {
                    return (
                      <video
                        key={currentVideo}
                        src={currentVideo}
                        controls
                        className="w-full h-full object-contain bg-black"
                        onError={() => {
                          console.error('Erreur de chargement vidéo');
                          setCurrentVideo(null);
                        }}
                      >
                        Votre navigateur ne supporte pas la lecture vidéo.
                      </video>
                    );
                  }
                }
                
                if (expose.photo_exposant) {
                  return (
                    <img
                      src={expose.photo_exposant}
                      alt={expose.prenom_exposant}
                      className="w-full h-full object-cover"
                    />
                  );
                }
                return (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-8xl opacity-50">🌙</span>
                  </div>
                );
              })()}
              
              {/* Overlay avec texte - caché si une vidéo est en cours de lecture */}
              {!(mediaMode === 'video' && currentVideo) && (
                <>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white pointer-events-none">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="text-4xl">🌙</span>
                      {expose.jour_ramadan && (
                        <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-semibold">
                          Jour {expose.jour_ramadan} - Ramadan {expose.annee_ramadan}
                        </span>
                      )}
                      {(() => {
                        const statusLabel = getStatusLabel(expose.status_auto, expose.status);
                        return (
                          <span className={`px-4 py-2 rounded-full text-sm font-semibold ${statusLabel.color}`}>
                            {statusLabel.icon} {statusLabel.text}
                          </span>
                        );
                      })()}
                    </div>
                    <h1 className="text-4xl sm:text-5xl font-bold mb-4">{expose.theme}</h1>
                  </div>
                </>
              )}
              
              {/* Titre minimisé quand vidéo en cours */}
              {mediaMode === 'video' && currentVideo && (
                <div className="absolute top-4 left-4 right-4 z-10">
                  <div className="bg-black/60 backdrop-blur-sm rounded-lg p-3 text-white">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-2xl">🌙</span>
                      {expose.jour_ramadan && (
                        <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold">
                          Jour {expose.jour_ramadan}
                        </span>
                      )}
                      {(() => {
                        const statusLabel = getStatusLabel(expose.status_auto, expose.status);
                        return (
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusLabel.color}`}>
                            {statusLabel.icon} {statusLabel.text}
                          </span>
                        );
                      })()}
                    </div>
                    <h1 className="text-xl sm:text-2xl font-bold line-clamp-1">{expose.theme}</h1>
                  </div>
                </div>
              )}
            </div>

            {/* Contenu */}
            <div className="p-8">
              {/* Infos exposant */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                  <div className="text-sm text-gray-600 mb-1">👤 Exposant</div>
                  <div className="font-bold text-gray-900">{expose.prenom_exposant} {expose.nom_exposant}</div>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                  <div className="text-sm text-gray-600 mb-1">🎓 Classe</div>
                  <div className="font-bold text-gray-900">{expose.classe}</div>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                  <div className="text-sm text-gray-600 mb-1">📅 Date</div>
                  <div className="font-bold text-gray-900">{new Date(expose.date_expose).toLocaleDateString('fr-FR')}</div>
                  {expose.jour_ramadan && (
                    <div className="text-sm text-purple-700 font-semibold">🌙 Jour {expose.jour_ramadan}</div>
                  )}
                </div>
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                  <div className="text-sm text-gray-600 mb-1">📅 Année Ramadan</div>
                  <div className="font-bold text-gray-900">{expose.annee_ramadan}</div>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center space-x-6 mb-8 pb-8 border-b border-gray-200">
                <div className="flex items-center space-x-2 text-gray-600">
                  <span>👁️</span>
                  <span className="font-semibold">{expose.nombre_vues || 0} vues</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-600">
                  <span>❤️</span>
                  <span className="font-semibold">{expose.nombre_likes || 0} likes</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-600">
                  <span>💬</span>
                  <span className="font-semibold">{expose.nombre_commentaires || 0} commentaires</span>
                </div>
              </div>

              {/* Description */}
              {expose.description && (
                <div className="mb-8">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Description</h2>
                  <p className="text-gray-700 text-base sm:text-lg leading-relaxed break-words overflow-wrap-anywhere">{expose.description}</p>
                </div>
              )}

              {/* Actions */}
              {user && (
                <div className="mb-8">
                  <button
                    onClick={handleLike}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                      expose.is_liked
                        ? 'bg-red-500 hover:bg-red-600 text-white'
                        : 'bg-purple-600 hover:bg-purple-700 text-white'
                    }`}
                  >
                    {expose.is_liked ? '❤️ Liké' : '🤍 Liker'}
                  </button>
                </div>
              )}

              {/* Section Médias */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">📺 Médias</h2>
                  {hasMedia && (
                    <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setMediaMode('video')}
                        className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                          mediaMode === 'video'
                            ? 'bg-purple-600 text-white shadow-md'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        📺 Lire ici
                      </button>
                      <button
                        onClick={() => setMediaMode('external')}
                        className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                          mediaMode === 'external'
                            ? 'bg-purple-600 text-white shadow-md'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        🔗 Lien externe
                      </button>
                    </div>
                  )}
                </div>

                {/* Contrôles vidéo/liens */}
                <div className="flex flex-wrap gap-4 mb-6">
                  {expose.mini_video ? (
                    <button
                      onClick={() => handleVideoChange(expose.mini_video!)}
                      className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center space-x-2"
                    >
                      <span>🎬</span>
                      <span>Mini Vidéo</span>
                    </button>
                  ) : (
                    <div className="px-6 py-3 bg-gray-200 text-gray-500 rounded-xl font-semibold inline-flex items-center space-x-2 cursor-not-allowed">
                      <span>🎬</span>
                      <span>Mini Vidéo (Sera disponible après l'exposé)</span>
                    </div>
                  )}
                  
                  {expose.lien_youtube ? (
                    <button
                      onClick={() => handleVideoChange(expose.lien_youtube!)}
                      className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center space-x-2"
                    >
                      <span>📺</span>
                      <span>YouTube</span>
                    </button>
                  ) : (
                    <div className="px-6 py-3 bg-gray-200 text-gray-500 rounded-xl font-semibold inline-flex items-center space-x-2 cursor-not-allowed">
                      <span>📺</span>
                      <span>YouTube (Sera disponible après l'exposé)</span>
                    </div>
                  )}
                  
                  {expose.lien_tiktok ? (
                    <button
                      onClick={() => handleVideoChange(expose.lien_tiktok!)}
                      className="px-6 py-3 bg-gray-900 hover:bg-black text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center space-x-2"
                    >
                      <span>🎵</span>
                      <span>TikTok</span>
                    </button>
                  ) : (
                    <div className="px-6 py-3 bg-gray-200 text-gray-500 rounded-xl font-semibold inline-flex items-center space-x-2 cursor-not-allowed">
                      <span>🎵</span>
                      <span>TikTok (Sera disponible après l'exposé)</span>
                    </div>
                  )}
                  
                  {expose.lien_instagram ? (
                    <button
                      onClick={() => handleVideoChange(expose.lien_instagram!)}
                      className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center space-x-2"
                    >
                      <span>📷</span>
                      <span>Instagram</span>
                    </button>
                  ) : (
                    <div className="px-6 py-3 bg-gray-200 text-gray-500 rounded-xl font-semibold inline-flex items-center space-x-2 cursor-not-allowed">
                      <span>📷</span>
                      <span>Instagram (Sera disponible après l'exposé)</span>
                    </div>
                  )}
                </div>

                {/* Lecteur audio intégré */}
                {expose.audio_complet ? (
                  <div className="bg-gradient-to-r from-purple-50 to-green-50 rounded-2xl p-6 border border-purple-200 shadow-lg">
                    <div className="flex items-center space-x-4 mb-4">
                      <span className="text-3xl">🎧</span>
                      <h3 className="text-xl font-bold text-gray-900">Audio Complet</h3>
                    </div>
                    <audio
                      controls
                      className="w-full"
                      src={expose.audio_complet}
                    >
                      Votre navigateur ne supporte pas la lecture audio.
                    </audio>
                    <div className="mt-4">
                      <a
                        href={expose.audio_complet}
                        download
                        className="inline-flex items-center space-x-2 text-purple-700 hover:text-purple-800 font-semibold"
                      >
                        <span>⬇️</span>
                        <span>Télécharger l'audio</span>
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-100 rounded-2xl p-6 border border-gray-300">
                    <div className="flex items-center space-x-4">
                      <span className="text-3xl">🎧</span>
                      <div>
                        <h3 className="text-xl font-bold text-gray-700">Audio Complet</h3>
                        <p className="text-gray-500">Sera disponible après l'exposé</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Commentaires */}
      <section className="relative bg-gradient-to-br from-purple-50/30 via-white to-green-50/50 py-10 sm:py-14 md:py-16 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-1/4 w-72 h-72 bg-purple-200/15 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-purple-100/50 p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              💬 Commentaires ({expose.nombre_commentaires || 0})
            </h2>

            {user ? (
              <form onSubmit={handleComment} className="mb-8 p-6 bg-purple-50 rounded-xl border border-purple-200">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Ajouter un commentaire..."
                  required
                  className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-white mb-4 min-h-[100px]"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {submitting ? 'Envoi...' : 'Commenter'}
                </button>
              </form>
            ) : (
              <div className="mb-8 p-6 bg-yellow-50 rounded-xl border border-yellow-200">
                <p className="text-gray-700">
                  <Link to="/login" className="text-purple-700 hover:text-purple-800 font-semibold underline">
                    Connectez-vous
                  </Link>
                  {' '}pour commenter
                </p>
              </div>
            )}

            <div className="space-y-4">
              {expose.commentaires_approuves && expose.commentaires_approuves.length > 0 ? (
                expose.commentaires_approuves.map((comment: Comment) => {
                  const isOwner = user && comment.utilisateur.id === user.id;
                  const isEditing = editingCommentId === comment.id;
                  
                  return (
                    <div
                      key={comment.id}
                      className="bg-gray-50 rounded-xl p-6 border border-gray-200 animate-slide-up"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                            {(comment.utilisateur.first_name || comment.utilisateur.username || 'U')[0].toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-gray-900 truncate">
                              {comment.utilisateur.first_name || comment.utilisateur.username}
                            </div>
                            <div className="text-sm text-gray-500 truncate">
                              {new Date(comment.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </div>
                        </div>
                        {isOwner && !isEditing && (
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <button
                              onClick={() => handleEditComment(comment)}
                              className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-lg hover:bg-blue-50 transition-all whitespace-nowrap"
                              disabled={submitting || deletingCommentId !== null}
                            >
                              <span className="hidden sm:inline">✏️ Modifier</span>
                              <span className="sm:hidden">✏️</span>
                            </button>
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="text-red-600 hover:text-red-800 text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-lg hover:bg-red-50 transition-all whitespace-nowrap"
                              disabled={submitting || deletingCommentId === comment.id}
                            >
                              {deletingCommentId === comment.id ? (
                                <span className="text-xs">Suppression...</span>
                              ) : (
                                <>
                                  <span className="hidden sm:inline">🗑️ Supprimer</span>
                                  <span className="sm:hidden">🗑️</span>
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                      {isEditing ? (
                        <div className="space-y-3">
                          <textarea
                            value={editingCommentText}
                            onChange={(e) => setEditingCommentText(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-white min-h-[100px]"
                            placeholder="Modifier votre commentaire..."
                          />
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-2">
                            <button
                              onClick={() => handleSaveEdit(comment.id)}
                              disabled={submitting || !editingCommentText.trim()}
                              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                            >
                              {submitting ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={submitting}
                              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-700 leading-relaxed">{comment.contenu}</p>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="text-6xl mb-4">💬</div>
                  <p className="text-gray-600 text-lg">Aucun commentaire pour le moment</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

